import { useCallback, useState } from 'preact/hooks';

import { usePlayerState } from '../../hooks/usePlayerState';
import { RewindButton } from '../buttons/RewindButton';
import { ForwardButton } from '../buttons/ForwardButton';
import { TogglePlayButton } from '../buttons/TogglePlayButton';
import { ReplayButton } from '../buttons/ReplayButton';
import { useStreamType } from '../../hooks/useStreamType';
import { useCast } from '../../hooks/CastProvider';
import { PlayNextButton } from '../buttons/PlayNextButton';
import { useJWPlayerEvent } from '../../hooks/useJWPlayerEvent';
import { useNextAsset } from '../../hooks/NextAssetProvider/NextAssetProvider';
import { isLiveStream } from '../../../../utils/player/asset';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';
import { useViewportType } from '../../hooks/useViewportType';
import { SponsorLink } from '../SponsorLink/SponsorLink';
import cn from 'classnames';
import { hasEditorialLink, hasSponsorData } from '../../utils/assetHelpers';

export const PlaybackButtons = ({ isVisible }: { isVisible: boolean }) => {
  const state = usePlayerState();
  const { canSeek } = useStreamType();
  const { isActive: isCasting } = useCast();
  const { nextAsset } = useNextAsset();
  const { asset } = useCurrentAsset();
  const { isMobile } = useViewportType();
  const size = isMobile ? 'medium' : 'large';

  const [displayPlayResumeButton, setDisplayPlayResumeButton] = useState(false);

  const handleTime = useCallback(
    (params: jwplayer.TimeParam) => {
      const { position } = params;
      const { duration } = params;

      if (position > 0.95 * duration) {
        setDisplayPlayResumeButton(true);
      } else if (displayPlayResumeButton) {
        setDisplayPlayResumeButton(false);
      }
    },
    [displayPlayResumeButton]
  );

  useJWPlayerEvent('time', handleTime);

  const isLinkAvailable =
    asset && (hasSponsorData(asset) || hasEditorialLink(asset));

  const shouldHideSeekButtons = (isLinkAvailable && isMobile) || !canSeek;

  let content;
  if (state === 'complete') {
    content = <ReplayButton color="video-ui" size={size} />;
  } else {
    content = (
      <span
        className={cn('vgp-playback-buttons-container', {
          'vgp-playback-buttons-container--hidden':
            !isVisible && isLinkAvailable,
          'vgp-playback-buttons-container--hidden-mobile':
            !isVisible && isMobile && isLinkAvailable,
        })}
      >
        <span>
          {!shouldHideSeekButtons && (
            <RewindButton color="video-ui" size={size} />
          )}
          <TogglePlayButton color="primary" size={size} />
          {isCasting &&
          displayPlayResumeButton &&
          nextAsset &&
          !isLiveStream(asset!) ? (
            <PlayNextButton color="video-ui" size={size} />
          ) : (
            !shouldHideSeekButtons && (
              <ForwardButton color="video-ui" size={size} />
            )
          )}
        </span>
        <SponsorLink />
      </span>
    );
  }

  return <div className="vgp-buttons vgp-playback-buttons">{content}</div>;
};
